import { Color, Icon, LineHeight, SkeletonText, Text } from '@billy/design-system'
import { ReactElement, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import * as Styled from './styles'

interface ReviewEntryAiReasoningProps {
  isLoading: boolean
  reasoning: string | null
}

const COLLAPSED_HEIGHT = Number.parseInt(LineHeight.TextBodyMD, 10)
const SKELETON_WIDTH = 130

export const ReviewEntryAiReasoning = ({ isLoading, reasoning }: ReviewEntryAiReasoningProps): ReactElement => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasExpandedOnce, setHasExpandedOnce] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [expandedHeight, setExpandedHeight] = useState(0)

  const [isAnimated, setIsAnimated] = useState(false)

  useLayoutEffect(() => {
    setIsAnimated(false)
  }, [isLoading, reasoning])

  const hasToggle = !isLoading && !!reasoning
  const isCollapsedLineVisible = isLoading || hasToggle

  useLayoutEffect(() => {
    if (isExpanded && wrapperRef.current) {
      setExpandedHeight(wrapperRef.current.scrollHeight)
    }
  }, [isExpanded, reasoning])

  let maxHeight = 0
  if (isExpanded) {
    maxHeight = expandedHeight || COLLAPSED_HEIGHT
  } else if (isCollapsedLineVisible) {
    maxHeight = COLLAPSED_HEIGHT
  }

  return (
    <Styled.ReviewEntryAiReasoningWrapper
      data-testid="review-entry-ai-reasoning"
      isAnimated={isAnimated}
      maxHeight={maxHeight}
      ref={wrapperRef}
    >
      {isLoading && <SkeletonText variant="bodyMD" width={SKELETON_WIDTH} />}
      {hasToggle && (
        <>
          <Styled.ReviewEntryAiReasoningToggle
            aria-expanded={isExpanded}
            onClick={() => {
              setIsAnimated(true)
              setIsExpanded((current) => !current)
              setHasExpandedOnce(true)
            }}
            type="button"
          >
            <Text color={Color.Purple600} variant="bodyMD" weight="medium">
              {t('review_entries.detail.reasoning.toggle')}
            </Text>
            <Icon color={Color.Purple600} icon={isExpanded ? 'chevronUp' : 'chevronDown'} size="sm" />
          </Styled.ReviewEntryAiReasoningToggle>
          {hasExpandedOnce && (
            <Text aria-hidden={!isExpanded} color={Color.Grey600} variant="bodyMD">
              {reasoning}
            </Text>
          )}
        </>
      )}
    </Styled.ReviewEntryAiReasoningWrapper>
  )
}
